import { COLOR_RULE, INPUT_TYPE, PLOT_TYPE, THEME_COLOR, MA_TYPE, PRICE_SOURCE } from "./indicatorTypes";
import { PALETTE } from "./palette";

const DEFAULT_LINE_WIDTH_PX = 1;

/**
 * Short builders of indicator definitions (definitions/*.js), so every
 * definition reads like the PO settings modal.
 */

/** Decimals of a whole number input or level. */
export const INTEGER_PRECISION = 0;

/**
 * Whole number input, e.g. a period, with the PO limits.
 *
 * @param {string} key
 * @param {string} label
 * @param {number} defaultValue
 * @param {{ min?: number, max: number }} limits
 */
export const periodInput = (key, label, defaultValue, { min = 1, max }) => ({
  key,
  label,
  type: INPUT_TYPE.NUMBER,
  defaultValue,
  min,
  max,
  precision: INTEGER_PRECISION,
});

/**
 * Number input with decimals, e.g. a deviation or a multiplier, with the PO limits.
 *
 * @param {string} key
 * @param {string} label
 * @param {number} defaultValue
 * @param {{ min: number, max: number, precision: number }} limits - precision: decimals kept
 */
export const numberInput = (key, label, defaultValue, { min, max, precision }) => ({
  key,
  label,
  type: INPUT_TYPE.NUMBER,
  defaultValue,
  min,
  max,
  precision,
});

/**
 * Limits of the levels typed in the Styles tab (`levelRules` of a
 * definition); levels of a definition without them are not limited, as in PO.
 *
 * @param {number} min
 * @param {number} max
 * @param {number} [precision] - decimals kept
 * @returns {{ min: number, max: number, precision: number }}
 */
export const levelRules = (min, max, precision = INTEGER_PRECISION) => ({ min, max, precision });

export const maTypeInput = (key = "maType", label = "MA Type", defaultValue = MA_TYPE.SMA) => ({
  key,
  label,
  type: INPUT_TYPE.SELECT,
  defaultValue,
  options: Object.values(MA_TYPE),
});

export const sourceInput = (key = "source", label = "Source", defaultValue = PRICE_SOURCE.CLOSE) => ({
  key,
  label,
  type: INPUT_TYPE.SELECT,
  defaultValue,
  options: Object.values(PRICE_SOURCE),
});

/**
 * @param {string} key - name of the values in the calculated series
 * @param {string} label - as in the Styles tab of PO
 * @param {string} color - PALETTE name or THEME_COLOR value
 * @param {object} [options] - width (CSS px), shift (bars, a number or (inputs) => number)
 */
export const linePlot = (key, label, color, { width = DEFAULT_LINE_WIDTH_PX, shift = 0 } = {}) => ({
  key,
  label,
  type: PLOT_TYPE.LINE,
  color,
  width,
  shift,
});

/**
 * @param {string} key
 * @param {string} label
 * @param {string} colorRule - COLOR_RULE value; COLOR_RULE.SINGLE has no down colour
 * @param {object} [colors] - up / down: PALETTE names or THEME_COLOR values;
 *   LIME / RED by default, as in PO
 */
export const histogramPlot = (
  key,
  label,
  colorRule,
  { up = PALETTE.LIME, down = PALETTE.RED } = {},
) => ({
  key,
  label,
  type: PLOT_TYPE.HISTOGRAM,
  color: up,
  downColor: colorRule === COLOR_RULE.SINGLE ? null : down,
  colorRule,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

export const dotsPlot = (key, label, color) => ({
  key,
  label,
  type: PLOT_TYPE.DOTS,
  color,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

/** @param {boolean} isAbove - marks above the highs (true) or below the lows */
export const markersPlot = (key, label, color, isAbove) => ({
  key,
  label,
  type: PLOT_TYPE.MARKERS,
  color,
  isAbove,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

/** @param {string[]} between - keys of the two lines the background fills */
export const fillPlot = (key, label, color, between) => ({
  key,
  label,
  type: PLOT_TYPE.FILL,
  color,
  between,
  width: 0,
  shift: 0,
});

/**
 * Horizontal level of an oscillator (RSI 70 / 30), each with its own colour
 * and width as in PO.
 *
 * @param {number} value
 * @param {string} [color] - PALETTE name or THEME_COLOR value
 * @param {number} [width] - CSS px
 */
export const level = (value, color = THEME_COLOR.LEVEL, width = DEFAULT_LINE_WIDTH_PX) => ({ value, color, width });
